import { XMarkIcon } from '@heroicons/react/24/outline';
import { useMemo, useRef } from 'react';
import { Button, Modal } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { useUserContext } from 'src/providers/UserProvider/User.context';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { useCancelSubscriptionContext } from '../CancelSubscription.context';
import { retentionBookingUrl } from '../CancelSubscription.functions';
import { buildRetentionBookingPageUrl } from './SchedulingModal.functions';
import { tSchedulingModalProps } from './SchedulingModal.types';

export default function SchedulingModal(props: tSchedulingModalProps) {
  const { isOpen, onClose } = props;
  const { cancellationReason } = useCancelSubscriptionContext();
  const { t } = useTranslation('settings/SubscriptionManager');

  // The configured booking page for the reason — a HubSpot meetings page or a
  // Calendly event. Null when this environment has none: the CTA that opens
  // this modal is hidden then, and the modal renders nothing rather than an
  // empty scheduler.
  const bookingUrl = retentionBookingUrl(cancellationReason);
  const { currentUser } = useUserContext();
  const { currentWorkspace, workspaceInfo } = useWorkspaceContext();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const bookingPageUrl = useMemo(
    () =>
      bookingUrl
        ? buildRetentionBookingPageUrl({
            bookingUrl,
            contact: {
              name: currentUser?.name,
              firstName: currentUser?.firstName,
              lastName: currentUser?.lastName,
              email: currentUser?.email,
              workspacePhone: workspaceInfo?.phoneNumber,
              workspaceId: currentWorkspace?.id,
            },
            now: new Date(),
          })
        : null,
    [bookingUrl, currentUser, currentWorkspace, workspaceInfo],
  );

  if (!bookingPageUrl) {
    return null;
  }

  return (
    <Modal open={isOpen} className="max-w-[804px] rounded-lg p-0 relative">
      <Button
        type="button"
        color="ghost"
        size="sm"
        shape="circle"
        className="absolute top-4 right-4 z-10"
        onClick={onClose}
        aria-label={t('cancelSubscription.offer.close')}
      >
        <XMarkIcon aria-hidden="true" className="size-5" />
      </Button>
      <Modal.Body className="p-0">
        <div className="relative w-full min-h-[700px]">
          <iframe
            ref={iframeRef}
            src={bookingPageUrl}
            width="100%"
            height="100%"
            title={t('cancelSubscription.schedulingModal.title')}
            className="absolute inset-0 w-full h-full min-h-[700px] border-0"
          />
        </div>
      </Modal.Body>
    </Modal>
  );
}
